import { diaSemanaYFecha } from './vigenciaHorario';
import { formatoHorario, type FilaFiltrable } from './tableFilters';

/**
 * Bajas de turnos (0399, diseño en docs/superpowers/specs/2026-11-04-bajas-de-turnos-design.md): lo que decide la
 * pestaña «Bajas» de Asignaciones. Quién puede, qué se borra y qué se bloquea lo decide el servidor
 * (`dar_de_baja_jornadas`); esto decide qué se ofrece y qué se manda.
 */

export interface PersonaDelTurno {
  nombre: string;
  /** `propuesta` o `aprobada`: las rechazadas no tienen el turno. */
  estado: string;
}

export interface JornadaParaBaja {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  pdv: { id: string; code: string; name: string } | null;
  personas: PersonaDelTurno[];
  /** Alguien marcó asistencia: no se da de baja, se corrige en Validación (decisión del usuario, 3-nov). */
  conMarca: boolean;
}

export type EstadoParaBaja = 'libre' | 'con_persona' | 'con_marca';

export const ETIQUETA_ESTADO: Record<EstadoParaBaja, string> = {
  libre: 'Sin persona',
  con_persona: 'Con persona',
  con_marca: 'Con marca (no se da de baja)',
};

export function estadoParaBaja(j: JornadaParaBaja): EstadoParaBaja {
  if (j.conMarca) return 'con_marca';
  return j.personas.length > 0 ? 'con_persona' : 'libre';
}

const sePuede = (j: JornadaParaBaja) => !j.conMarca;

/** Proyección al motor de filtros compartido (`tableFilters`). */
export function filaDeBaja(j: JornadaParaBaja): FilaFiltrable {
  return {
    date: j.date,
    pdvKey: j.pdv?.code ?? '',
    horario: formatoHorario(j.start_time, j.end_time),
    slot: j.slot_index,
    estado: estadoParaBaja(j),
    nombre: j.personas.length ? j.personas.map((p) => p.nombre).join(', ') : null,
    dni: null,
  };
}

/** Cómo se ve la casilla de «seleccionar todo lo filtrado», contando solo lo que se puede dar de baja. */
export function casillaDeTodo(visibles: JornadaParaBaja[], sel: Set<string>): 'ninguna' | 'algunas' | 'todas' {
  const posibles = visibles.filter(sePuede);
  const marcadas = posibles.filter((j) => sel.has(j.id)).length;
  if (posibles.length === 0 || marcadas === 0) return 'ninguna';
  return marcadas === posibles.length ? 'todas' : 'algunas';
}

/** Tocar la casilla de todo: si ya estaba todo, desmarca lo visible; si no, marca todo lo visible que se pueda. */
export function alternarTodo(visibles: JornadaParaBaja[], sel: Set<string>): Set<string> {
  const nueva = new Set(sel);
  const posibles = visibles.filter(sePuede);
  if (casillaDeTodo(visibles, sel) === 'todas') posibles.forEach((j) => nueva.delete(j.id));
  else posibles.forEach((j) => nueva.add(j.id));
  return nueva;
}

/**
 * Lo que se manda al servidor: lo seleccionado QUE ESTÁ A LA VISTA y se puede dar de baja.
 *
 * Una selección que sobrevive a un cambio de filtro daría de baja turnos que la persona ya no está mirando: la lista
 * que confirma tiene que ser la que ve.
 */
export function idsParaDarDeBaja(visibles: JornadaParaBaja[], sel: Set<string>): string[] {
  return visibles.filter((j) => sePuede(j) && sel.has(j.id)).map((j) => j.id);
}

const lugar = (j: JornadaParaBaja) => (j.pdv ? `${j.pdv.code} — ${j.pdv.name}` : 'Sin PDV');

/** Para la confirmación: cuántos turnos y a quién hay que avisar. */
export function resumenDeSeleccion(visibles: JornadaParaBaja[], sel: Set<string>): {
  total: number;
  conPersona: number;
  personas: string[];
} {
  const elegidas = visibles.filter((j) => sePuede(j) && sel.has(j.id));
  const personas = elegidas.flatMap((j) =>
    j.personas.map((p) => `${p.nombre} · ${diaSemanaYFecha(j.date)} · ${lugar(j)}`));
  return {
    total: elegidas.length,
    conPersona: elegidas.filter((j) => j.personas.length > 0).length,
    personas,
  };
}

/** Lo que devuelve `dar_de_baja_jornadas`. */
export interface ResultadoBaja {
  pedidas: number;
  dadas_de_baja: number;
  no_encontradas: number;
  bloqueadas: { id: string; fecha: string; pdv: string | null; motivo: string }[];
  personas: { nombre: string; estado: string; fecha: string; pdv: string | null }[];
}

const turnos = (n: number) => `${n} turno${n === 1 ? '' : 's'}`;

export function textoDelResultado(r: ResultadoBaja): string {
  const partes = [
    r.dadas_de_baja === 1 ? 'Se dio de baja 1 turno.' : `Se dieron de baja ${turnos(r.dadas_de_baja)}.`,
  ];
  if (r.bloqueadas.length > 0) {
    partes.push(`${r.bloqueadas.length} no se ${r.bloqueadas.length === 1 ? 'pudo' : 'pudieron'}: `
      + 'tiene marca o registros de campo y se corrige en Validación.');
  }
  if (r.no_encontradas > 0) {
    partes.push(`${r.no_encontradas} ya no estaba${r.no_encontradas === 1 ? '' : 'n'}: alguien la cambió mientras tanto.`);
  }
  return partes.join(' ');
}
